import type { CheckoutSession } from "@/lib/api/checkout.schema";
import type { MarketPreview } from "@/lib/markets";
import { formatAmount, formatDisplayAmount } from "./payment.utils";

/**
 * Amount formatting for a checkout session. Session-currency amounts are
 * shown in the previewed market's currency when a preview conversion
 * exists, else honour the merchant's display currency (informational
 * secondary currency with a rate), else as-is.
 */
export function createSessionAmountFormatter(
  session: CheckoutSession,
  preview?: MarketPreview | null,
): {
  /** Any session-currency amount (line items, custom amount, totals) */
  formatPrice: (amount: number) => string;
  /** The session's fixed amount - prefers the server-converted figure */
  formatFixedAmount: () => string;
} {
  const view =
    preview && preview.market.currency !== session.currency ? preview : null;
  const display =
    !view &&
    session.display_currency &&
    session.display_currency !== session.currency &&
    session.display_amount
      ? session.display_amount
      : null;

  const formatPrice = (amount: number) => {
    if (view) return formatAmount(amount * view.rate, view.market.currency);
    if (display) return formatDisplayAmount(amount * display.rate, display.currency);
    return formatAmount(amount, session.currency);
  };

  const formatFixedAmount = () =>
    display
      ? formatDisplayAmount(display.amount, display.currency)
      : formatPrice(session.amount);

  return { formatPrice, formatFixedAmount };
}
